import React, { useRef, useEffect, useContext, useState } from 'react';
import PropTypes from 'prop-types';
import RealexHpp from '../lib/rxp-js/RealexHpp';
import axios from 'axios';
import { DonationAppContext } from './DonationApp';
import DonationProcessing from './DonationProcessing';

/**
 * Component to render the Global payments hosted payment page via iframe.
 *
 * @params See propTypes
 * @returns {JSX.Element}
 * @component
 */
const GlobalPaymentsHPP = ({ donation }) => {
  /**
   * The values from DonationAppContext
   */
  const { APP_STATES, setDonationAppState } = useContext(DonationAppContext);

  const [globalPaymentsHppState, setGlobalPaymentsHppState] = useState({
    returned: false,
    responseData: {}
  });

  const formContainer = useRef(null);

  const initGlobalPay = () => {
    RealexHpp.setHppUrl(process.env.REACT_APP_GLOBAL_PAYMENTS_URL);
    RealexHpp.embedded.init('autoload', 'global_pay',
      function (globalPaymentsResponse, close) {
        setGlobalPaymentsHppState({
          returned: true,
          responseData: globalPaymentsResponse
        });

        close();
      },
      donation, {
        onResize: function (styleData) {
          document.getElementById('global_pay').style.height = styleData.height;
        }
      });
  };

  const setDonationProcessedApplicationState = () => {
    setDonationAppState(prevState => {
      return {
        ...prevState,
        state: APP_STATES.DONATION_POST_PROCESSED,
        globalPaymentsResponse: globalPaymentsHppState.responseData
      };
    });
  };

  const processSuccessfulPayment = (globalPaymentsResponse) => {
    const postProcessUrl = `${process.env.REACT_APP_HE_ECOM_API}/PostProcess/Dynamics/${globalPaymentsResponse.ORDER_ID}`;

    axios.get(postProcessUrl, {
      headers: {
        siteId: process.env.REACT_APP_HE_SITE_ID
      }
    })
      .then(() => {
        setDonationProcessedApplicationState(globalPaymentsResponse);
      })
      .catch(() => {
        setDonationAppState(prevState => {
          return {
            ...prevState,
            state: APP_STATES.ERROR,
            error: (
              <>
                <p>We’re sorry – your donation was successful but there was a problem recording the details in our
                  system.</p>
                <p>
                  We greatly value your generous support, and would like to make sure we have a proper record of your
                  donation so it’s put towards the right cause.
                </p>
                <p>
                  Please contact us via email so we can make a record of your donation: <a className="text-break" href="mailto:info@historicenglandfoundation.org.uk">info@HistoricEnglandFoundation.org.uk</a>
                </p>
              </>
            )
          };
        });
      });
  };

  const processFailedPayment = (globalPaymentsResponse) => {
    const failedPaymentUrl = `${process.env.REACT_APP_HE_ECOM_API}/PostProcess/FailedPayment/${globalPaymentsResponse.ORDER_ID}`;

    axios.get(failedPaymentUrl, {
      headers: {
        siteId: process.env.REACT_APP_HE_SITE_ID
      }
    })
      .then(() => {
        setDonationProcessedApplicationState();
      })
      .catch(() => {
        //
      });
  };

  const processPayment = () => {
    if (globalPaymentsHppState.responseData.AUTHCODE) {
      processSuccessfulPayment(globalPaymentsHppState.responseData);
    } else {
      processFailedPayment(globalPaymentsHppState.responseData);
    }
  };

  useEffect(() => {
    initGlobalPay();
  }, []);

  useEffect(() => {
    if (globalPaymentsHppState.returned) {
      processPayment();
    }
  }, [globalPaymentsHppState]);

  useEffect(() => {
    if (formContainer?.current) {
      const { top } = formContainer.current.getBoundingClientRect();
      window.scrollTo(0, top + window.scrollY - 100);
    }
  }, [globalPaymentsHppState]);

  switch (globalPaymentsHppState.returned) {
    case true:
      return <DonationProcessing/>;

    case false:
    default:
      return (
        <div
          ref={formContainer}
          className="l-section__content-wrapper align-items-center mt-12 pt-0 pb-10 pb-md-20 px-md-20">
          <iframe
            id="global_pay"
            frameBorder="0"
            className="c-globalPaymentsForm w-100"/>
        </div>
      );
  }
};

GlobalPaymentsHPP.propTypes = {
  /**
   * An object containing the generated HPP donation data.
   */
  donation: PropTypes.object.isRequired
};

export default GlobalPaymentsHPP;
